import React, { useState } from "react";
import { LogOut, Users, KeyRound, ChevronDown, ShieldCheck, Images, Globe, DatabaseBackup } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth, formatApiError } from "@/context/AuthContext";
import TeamDialog from "@/components/TeamDialog";
import GalleryManager from "@/components/GalleryManager";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;

export default function UserMenu() {
  const { user, logout, isOwner } = useAuth();
  const [teamOpen, setTeamOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  if (!user) return null;

  const changePassword = async () => {
    const current = window.prompt("Current password:");
    if (!current) return;
    const next = window.prompt("New password (min 6 characters):");
    if (!next) return;
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next });
      toast.success("Password changed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not change password"));
    }
  };

  const initials = (user.name || user.email).slice(0, 2).toUpperCase();
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/60 pl-1.5 pr-2 py-1 hover:border-slate-600 transition-colors">
            <span className="w-7 h-7 rounded-lg bg-amber-400 text-slate-950 grid place-items-center text-[11px] font-black">{initials}</span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60 bg-[#111827] border-slate-700 text-slate-100">
          <DropdownMenuLabel className="font-normal">
            <div className="font-semibold truncate">{user.name || user.email}</div>
            <div className="text-xs text-slate-400 truncate flex items-center gap-1">{isOwner && <ShieldCheck className="w-3 h-3 text-amber-400" />} {user.role} · {user.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-slate-800" />
          {isOwner && (
            <DropdownMenuItem onSelect={() => setTeamOpen(true)} data-testid="user-menu-team" className="gap-2 focus:bg-slate-800 focus:text-slate-100"><Users className="w-4 h-4" /> Team & access</DropdownMenuItem>
          )}
          {isOwner && (
            <DropdownMenuItem onSelect={() => setGalleryOpen(true)} data-testid="user-menu-gallery" className="gap-2 focus:bg-slate-800 focus:text-slate-100"><Images className="w-4 h-4" /> Website gallery</DropdownMenuItem>
          )}
          <DropdownMenuItem asChild data-testid="user-menu-backup" className="gap-2 focus:bg-slate-800 focus:text-slate-100"><Link to="/backup"><DatabaseBackup className="w-4 h-4" /> Backup &amp; export</Link></DropdownMenuItem>
          <DropdownMenuItem asChild data-testid="user-menu-site" className="gap-2 focus:bg-slate-800 focus:text-slate-100"><Link to="/"><Globe className="w-4 h-4" /> View public website</Link></DropdownMenuItem>
          <DropdownMenuItem onSelect={changePassword} data-testid="user-menu-password" className="gap-2 focus:bg-slate-800 focus:text-slate-100"><KeyRound className="w-4 h-4" /> Change password</DropdownMenuItem>
          <DropdownMenuSeparator className="bg-slate-800" />
          <DropdownMenuItem onSelect={logout} data-testid="user-menu-logout" className="gap-2 text-rose-300 focus:bg-rose-950/50 focus:text-rose-200"><LogOut className="w-4 h-4" /> Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <TeamDialog open={teamOpen} onOpenChange={setTeamOpen} />
      <GalleryManager open={galleryOpen} onOpenChange={setGalleryOpen} />
    </>
  );
}
